/**
 * Notification status / source / circuit badges + channel icons + copyable.
 * Ported 1:1 from the previous console's ui/* (colors, labels, dot semantics).
 */
import React, { useState } from 'react';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';
import Icon, { IconName } from './Icon';
import { useTheme } from '../../context/themeContext';
import type { ChannelType, CircuitState, NotificationStatus } from '../../schema/notification';

/* ── Status badge ── (colors from the log STATUS_META) */
const STATUS: Record<string, { dot: string; label: string; soft: string }> = {
  queued: { dot: '#94A3B8', label: 'queued', soft: '#F1F5F9' },
  processing: { dot: '#1D4ED8', label: 'processing', soft: '#EFF3FE' },
  delivered: { dot: '#2E7D32', label: 'delivered', soft: '#EBF6F1' },
  partial: { dot: '#F57C00', label: 'partial', soft: '#FDF4E3' },
  failed: { dot: '#C0392B', label: 'failed', soft: '#FCEAEA' },
  validation_failed: { dot: '#E65100', label: 'validation failed', soft: '#FDF0E3' },
  fallback_skipped: { dot: '#6A1B9A', label: 'fallback skipped', soft: '#F5EBFA' },
  quota_exceeded: { dot: '#C0392B', label: 'quota exceeded', soft: '#FCEAEA' },
};

export const StatusBadge: React.FC<{ status: NotificationStatus | string }> = ({ status }) => {
  const v = STATUS[status] || STATUS.queued;
  return (
    <View style={[s.badge, { backgroundColor: v.soft }]}>
      <View style={[s.dot, { backgroundColor: v.dot }]} />
      <Text style={[s.badgeText, { color: v.dot }]}>{v.label}</Text>
    </View>
  );
};

/* ── Source (module) badge ── */
const SOURCE_TINT: Record<string, string> = {
  works: '#1D4ED8', crm: '#16A34A', ticketing: '#6B21A8', calendar: '#E65100',
  cal: '#E65100', tickets: '#6B21A8', skplatform: '#0891B2',
};
export const SourceBadge: React.FC<{ source: string }> = ({ source }) => {
  const { theme: { colors } } = useTheme();
  const tint = SOURCE_TINT[source] || colors.ink3;
  return (
    <View style={[s.module, { borderColor: tint + '55' }]}>
      <Text style={[s.moduleText, { color: tint }]}>{source}</Text>
    </View>
  );
};

/* ── Circuit-breaker badge ── */
const CB: Record<string, { icon: IconName; label: string; color: string; soft: string }> = {
  CLOSED: { icon: 'lock', label: 'CLOSED', color: '#2E7D32', soft: '#EBF6F1' },
  OPEN: { icon: 'alert', label: 'OPEN', color: '#C0392B', soft: '#FCEAEA' },
  HALF_OPEN: { icon: 'cycle', label: 'HALF-OPEN', color: '#B87D0A', soft: '#FDF4E3' },
};
export const CircuitBadge: React.FC<{ state: CircuitState | string }> = ({ state }) => {
  const m = CB[state] || CB.CLOSED;
  return (
    <View style={[s.badge, { backgroundColor: m.soft }]}>
      {state === 'OPEN' && <View style={[s.dot, { backgroundColor: m.color }]} />}
      <Icon name={m.icon} size={11} color={m.color} />
      <Text style={[s.badgeText, { color: m.color, marginLeft: 4 }]}>{m.label}</Text>
    </View>
  );
};

/* ── Channel icon + row ── */
const CHANNEL_ICON: Record<string, IconName> = { email: 'mail', whatsapp: 'message', sms: 'phone', push: 'bell' };
const DOT_COLOR: Record<string, string> = { success: '#2E7D32', delivered: '#2E7D32', failed: '#C0392B', quota: '#C0392B', pending: '#94A3B8' };

export const ChannelBadge: React.FC<{ channel: string; status: string; size?: number }> = ({ channel, status, size = 22 }) => {
  const { theme: { colors } } = useTheme();
  return (
    <View style={[s.chan, { width: size, height: size, backgroundColor: colors.paper2, borderColor: colors.line }]}>
      <Icon name={CHANNEL_ICON[channel] || 'bell'} size={Math.floor(size * 0.5)} color={colors.ink2} />
      <View style={[s.chanDot, { backgroundColor: DOT_COLOR[status] || '#94A3B8' }]} />
    </View>
  );
};

export const ChannelRow: React.FC<{ channels: Array<{ type: string; status: string; dotStatus?: string }> }> = ({ channels }) => (
  <View style={s.chanRow}>
    {channels.map((c, i) => <ChannelBadge key={i} channel={c.type} status={c.dotStatus || c.status} />)}
  </View>
);

/* ── Copyable (click to copy, web clipboard) ── */
export const Copyable: React.FC<{ value: string; label?: string }> = ({ value, label }) => {
  const { theme: { colors, font } } = useTheme();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try { if (Platform.OS === 'web') navigator.clipboard?.writeText(value); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <Pressable onPress={copy} style={[s.copyable, { borderColor: colors.line, backgroundColor: colors.paper2 }]}>
      <Text style={{ fontFamily: font.mono, fontSize: 12, color: colors.ink2 }} numberOfLines={1}>{label || value}</Text>
      <Icon name={copied ? 'check' : 'copy'} size={11} color={copied ? '#2E7D32' : colors.ink3} />
    </Pressable>
  );
};

const s = StyleSheet.create({
  badge: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 2, gap: 5 },
  badgeText: { fontSize: 11, fontWeight: '600', textTransform: 'lowercase' },
  dot: { width: 6, height: 6, borderRadius: 3 },
  module: { alignSelf: 'flex-start', paddingHorizontal: 7, paddingVertical: 2, borderRadius: 2, borderWidth: 1 },
  moduleText: { fontSize: 11, fontWeight: '600' },
  chan: { borderRadius: 6, borderWidth: 1, alignItems: 'center', justifyContent: 'center', position: 'relative' },
  chanDot: { position: 'absolute', top: -2, right: -2, width: 7, height: 7, borderRadius: 4, borderWidth: 1, borderColor: '#fff' },
  chanRow: { flexDirection: 'row', gap: 6 },
  copyable: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 2, borderWidth: 1, alignSelf: 'flex-start', maxWidth: 340 },
});
